import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import CloseButton from 'react-bootstrap/CloseButton';
import { api } from '../config/api';
import { getDecryptedData } from '../utils/authStorage';
import { getClientDetails as fetchAllClients } from '../services/clientService';
import RequirementExtraFields, { REQUIREMENT_EXTRA_DEFAULTS, buildRequirementExtraPayload } from './ui/RequirementExtraFields';
import { REQUIREMENT_TYPE_OPTIONS } from '../utils/requirementType';
import {
  MIN_ASSESSMENTS,
  appendRequirementFormData,
  createEmptyAssessments,
  validateAssessmentsInput,
} from '../utils/requirementAssessments';
import { createRequirement } from '../services/requirementService';
import { CONTRACT_TYPE_OPTIONS, isValidContractType } from '../utils/requirementContractType';
import RequirementAssignMembers from './requirements/RequirementAssignMembers';
import { showSuccess, showError } from '../utils/toast';
import { getApiErrorMessage } from '../utils/apiError';
import { buildDuplicatePrefillFromRequirement } from '../utils/requirementDuplicatePrefill';

const fieldStyle = {
  width: '300px',
  textAlign: 'center',
  margin: '10px',
  border: '1px solid gray',
  borderRadius: '15px',
};

function RequirementForm({
  mode = 'admin',
  embedded = false,
  onClose,
  onSuccess,
  modalPayload = null,
}) {
  const [regId, setRegId] = useState('');
  const [client, setClient] = useState([]);
  const [typeOfContract, setTypeOfContract] = useState('');
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [location, setLocation] = useState('');
  const [sourceCtc, setSourceCtc] = useState('');
  const [ectc, setEctc] = useState('');
  const [qualification, setQualification] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [relevantExperience, setRelevantExperience] = useState('');
  const [skill, setSkill] = useState('');
  const [requirmentType, setRequirmentType] = useState('');
  const [role, setRole] = useState('');
  const [val, setVal] = useState(createEmptyAssessments());
  const [jdFile, setJdFile] = useState(null);
  const [selectedClient, setSelectedClient] = useState({ ClientId: '', ClientName: '' });
  const [extraFields, setExtraFields] = useState(REQUIREMENT_EXTRA_DEFAULTS);
  const [assignableMembers, setAssignableMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const userId = getDecryptedData('Id');
  const formId = mode === 'teamlead' ? 'tl-add-requirement-form' : 'admin-add-requirement-form';

  const handleExtraChange = (key, value) => {
    setExtraFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleClientSelect = (e) => {
    const selectedValue = JSON.parse(e.target.value);
    setSelectedClient({
      ClientId: selectedValue.ClientId,
      ClientName: selectedValue.ClientName,
    });
  };

  const generateRegID = () => {
    const randomClientCode = Math.floor(Math.random() * 10000);
    setRegId(randomClientCode.toString());
  };

  const loadClients = useCallback(async () => {
    try {
      if (mode === 'teamlead') {
        if (!userId) {
          setClient([]);
          return;
        }
        const { data } = await api.get(`/TlClients/${userId}`);
        setClient(Array.isArray(data.Client) ? data.Client : []);
        return;
      }

      const data = await fetchAllClients();
      setClient(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClient([]);
    }
  }, [mode, userId]);

  useEffect(() => {
    loadClients();
    generateRegID();
  }, [loadClients]);

  useEffect(() => {
    const duplicateRequirementId = modalPayload?.duplicateRequirementId;
    if (!duplicateRequirementId || mode !== 'admin') return undefined;

    let active = true;
    const loadDuplicateSource = async () => {
      generateRegID();
      try {
        const { data } = await api.get(`/getrequirements/${duplicateRequirementId}`);
        if (!active || !data) return;

        const prefill = buildDuplicatePrefillFromRequirement(data);
        setSelectedClient(prefill.selectedClient);
        setTypeOfContract(prefill.typeOfContract);
        setStartDate(prefill.startDate);
        setDuration(prefill.duration);
        setLocation(prefill.location);
        setSourceCtc(prefill.sourceCtc);
        setEctc(prefill.ectc);
        setQualification(prefill.qualification);
        setYearsExperience(prefill.yearsExperience);
        setRelevantExperience(prefill.relevantExperience);
        setSkill(prefill.skill);
        setRole(prefill.role);
        setRequirmentType(prefill.requirmentType);
        setVal(prefill.val);
        setExtraFields(prefill.extraFields);
        setSelectedMemberIds(prefill.selectedMemberIds);
        setJdFile(prefill.jdFile);
      } catch (error) {
        console.error('Failed to load requirement for duplicate:', error);
        showError(getApiErrorMessage(error, 'Unable to load requirement to duplicate.'));
      }
    };

    loadDuplicateSource();
    return () => {
      active = false;
    };
  }, [modalPayload?.duplicateRequirementId, mode]);

  useEffect(() => {
    if (mode !== 'admin') return undefined;

    let active = true;
    const loadMembers = async () => {
      setLoadingMembers(true);
      try {
        const { data } = await api.get('/api/assignable-members');
        if (active) setAssignableMembers(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load assignable members:', error);
        if (active) setAssignableMembers([]);
      } finally {
        if (active) setLoadingMembers(false);
      }
    };

    loadMembers();
    return () => {
      active = false;
    };
  }, [mode]);

  const handleAdd = () => {
    setVal((prev) => [...prev, { Assessment: '', Yoe: '' }]);
  };

  const handleChange = (event, index) => {
    const { name, value } = event.target;
    setVal((prev) => {
      const inputdata = [...prev];
      inputdata[index] = { ...inputdata[index], [name]: value };
      return inputdata;
    });
  };

  const handleDelete = (index) => {
    if (val.length <= MIN_ASSESSMENTS) {
      showError(`Minimum ${MIN_ASSESSMENTS} assessments are required.`);
      return;
    }
    setVal((prev) => prev.filter((_, i) => i !== index));
  };

  const handleJdChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setJdFile(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      showError('JD must be a PDF file.');
      event.target.value = '';
      setJdFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('JD PDF must be 5 MB or smaller.');
      event.target.value = '';
      setJdFile(null);
      return;
    }
    setJdFile(file);
  };

  const sendUserDataToDataBase = async () => {
    const assessmentValidation = validateAssessmentsInput(val);
    if (!assessmentValidation.ok) {
      showError(assessmentValidation.message);
      return;
    }

    if (!isValidContractType(typeOfContract)) {
      showError('Please select Type Of Contract (FTE, C2H, or FTE/C2H).');
      return;
    }

    const payload = {
      regId,
      client: selectedClient.ClientName,
      typeOfContract,
      startDate,
      duration,
      location,
      sourceCtc,
      ectc,
      qualification,
      yearsExperience,
      relevantExperience,
      skill,
      role,
      requirmentType,
      uploadedBy: userId,
      clientId: selectedClient.ClientId,
      ...buildRequirementExtraPayload(extraFields),
      assessments: assessmentValidation.assessments,
      update: 'New',
      ...(mode === 'admin' && selectedMemberIds.length
        ? { assignedMembers: selectedMemberIds }
        : {}),
    };

    const formData = new FormData();
    appendRequirementFormData(formData, payload, jdFile);

    try {
      const result = await createRequirement(formData);

      if (result.status === 'Success') {
        if (embedded && onSuccess) onSuccess(result.msg);
        else if (embedded && onClose) onClose();
        else {
          showSuccess(result.msg || 'Requirement created successfully ✓');
          setTimeout(() => window.location.reload(), 600);
        }
      } else {
        showError(result.msg || 'Failed to create requirement. Please try again.');
      }
    } catch (error) {
      showError(getApiErrorMessage(error, 'Unable to create requirement.'));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendUserDataToDataBase();
  };

  return (
    <div className={embedded ? 'hr-embedded-form' : ''}>
      <center>
        <Form
          id={formId}
          onSubmit={handleSubmit}
          style={embedded ? {} : { backgroundColor: 'blanchedalmond', margin: '15px', borderRadius: '20px', padding: '15px' }}
        >
          {!embedded && (
            <h2 style={{ textAlign: 'center', fontFamily: 'initial' }}>
              <b>
                <img style={{ width: '35px', borderRadius: '50px' }} src="/Images/icon.png" alt="" />
                {' '}
                Add New Requirement
              </b>
            </h2>
          )}
          {!embedded && <hr />}

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label><b>Reg Id</b></Form.Label>
              <Form.Control value={regId} style={fieldStyle} placeholder="Enter Reg Id" readOnly />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Client</b></Form.Label>
              <Form.Select style={fieldStyle} onChange={handleClientSelect}>
                <option value="">Choose...</option>
                {client.map((item, index) => (
                  <option
                    key={index}
                    value={JSON.stringify({ ClientId: item._id, ClientName: item.ClientName })}
                  >
                    {item.ClientName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Type Of Contract *</b></Form.Label>
              <Form.Select value={typeOfContract} onChange={(e) => setTypeOfContract(e.target.value)} style={fieldStyle} required>
                <option value="">Choose...</option>
                {CONTRACT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Options: FTE, C2H, FTE/C2H</Form.Text>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Start Date</b></Form.Label>
              <Form.Control value={startDate} onChange={(e) => setStartDate(e.target.value)} type="date" style={fieldStyle} />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Duration</b></Form.Label>
              <Form.Control value={duration} onChange={(e) => setDuration(e.target.value)} style={fieldStyle} placeholder="Enter Duration" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Location</b></Form.Label>
              <Form.Control value={location} onChange={(e) => setLocation(e.target.value)} style={fieldStyle} placeholder="Enter Location" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Source CTC</b></Form.Label>
              <Form.Control value={sourceCtc} onChange={(e) => setSourceCtc(e.target.value)} style={fieldStyle} placeholder="Enter Source CTC" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>ECTC</b></Form.Label>
              <Form.Control value={ectc} onChange={(e) => setEctc(e.target.value)} style={fieldStyle} placeholder="Enter ECTC" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Qualification</b></Form.Label>
              <Form.Control value={qualification} onChange={(e) => setQualification(e.target.value)} style={fieldStyle} placeholder="Enter Qualification" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Years of Experience</b></Form.Label>
              <Form.Control value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} style={fieldStyle} placeholder="Enter Years of Experience" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Relevant Experience</b></Form.Label>
              <Form.Control value={relevantExperience} onChange={(e) => setRelevantExperience(e.target.value)} style={fieldStyle} placeholder="Enter Relevant Experience" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Skill</b></Form.Label>
              <textarea value={skill} onChange={(e) => setSkill(e.target.value)} style={fieldStyle} placeholder="Enter Skills" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Role</b></Form.Label>
              <Form.Control value={role} onChange={(e) => setRole(e.target.value)} type="text" style={fieldStyle} placeholder="Enter Role" />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Requirement Type</b></Form.Label>
              <Form.Select value={requirmentType} onChange={(e) => setRequirmentType(e.target.value)} style={fieldStyle} required>
                <option value="">Choose...</option>
                {REQUIREMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label><b>Upload JD (PDF)</b></Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleJdChange}
                style={fieldStyle}
              />
              <Form.Text className="text-muted">Optional. PDF only, maximum 5 MB.</Form.Text>
            </Form.Group>

            <hr />
            <RequirementExtraFields values={extraFields} onChange={handleExtraChange} />
            <hr />

            {mode === 'admin' && (
              <>
                <RequirementAssignMembers
                  members={assignableMembers}
                  selectedMemberIds={selectedMemberIds}
                  onSelectionChange={setSelectedMemberIds}
                  loading={loadingMembers}
                />
                <hr />
              </>
            )}

            <Form.Group as={Col}>
              <div className="mb-2">
                <b>Assessment *</b>
                <div className="text-muted">Minimum {MIN_ASSESSMENTS} assessments are mandatory.</div>
              </div>
              {val.map((data, index) => (
                <Row key={index}>
                  <Form.Control
                    name="Assessment"
                    value={data.Assessment}
                    onChange={(event) => handleChange(event, index)}
                    style={{ ...fieldStyle, width: '50%' }}
                    placeholder="Enter Assessment"
                    required
                  />
                  <Form.Control
                    name="Yoe"
                    value={data.Yoe}
                    onChange={(event) => handleChange(event, index)}
                    style={{ ...fieldStyle, width: '100px' }}
                    placeholder="Enter Expected Rating"
                    required
                  />
                  {val.length > MIN_ASSESSMENTS ? (
                    <CloseButton onClick={() => handleDelete(index)} />
                  ) : null}
                </Row>
              ))}
              <hr />
              <Button onClick={handleAdd}>ADD Assessment</Button>
              <hr />
            </Form.Group>
          </Row>

          {!embedded && (
            <Button style={{ borderRadius: '15px' }} variant="primary" type="submit">
              <b>Submit</b>
            </Button>
          )}
        </Form>
      </center>
    </div>
  );
}

export default RequirementForm;
